import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import useLawFirmBondsDialogs from "./useLawFirmBondsDialogs";
import useLawFirmBondsFilters from "./useLawFirmBondsFilters";
import useLawFirmBondsTable from "./useLawFirmBondsTable";
import { getCancellationInstructionsList } from "../helpers/lawFirmBondsCancellationCopy";
import {
  buildEndorsementFormData,
  capitalizeFirst,
  getReleaseRiderUrl,
  isNameChangeEndorsement,
} from "../helpers/lawFirmBondsHelpers";
import { LAW_FIRM_BONDS_MODES } from "../helpers/lawFirmBondsPageConfig";

const lawFirmStatusOptions = ["Active", "Rejected", "To Be Cancelled", "Cancelled"];
const archiveStatusOptions = ["pending", "approved", "rejected"];

/** Composes Phase 5 split hooks: filters + table derivation + dialogs. Pagination/sort lifted here for reset wiring. */
export default function useLawFirmBondsData(mode) {
  const dispatch = useDispatch();
  const isTeamMember = mode === LAW_FIRM_BONDS_MODES.TEAM_MEMBER;

  const savedPage = useSelector((state) =>
    isTeamMember ? state.bondArchives?.page ?? 0 : state.lawFirmBonds?.page ?? 0
  );

  const [page, setPage] = useState(savedPage);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState("created_at");
  const [order, setOrder] = useState("desc");

  const resetPagination = useCallback(() => setPage(0), []);

  const filters = useLawFirmBondsFilters(mode, { resetPagination });

  const dialogs = useLawFirmBondsDialogs(mode, {
    filters: filters.filters,
    lawFirms: filters.lawFirms,
    lawfirmId: filters.lawfirmId,
    fetchInitialData: filters.fetchInitialData,
  });

  const handleSort = useCallback((column) => {
    const isAsc = orderBy === column && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(column);
  }, [orderBy, order]);

  const table = useLawFirmBondsTable({
    filteredData: filters.filteredData,
    allBonds: filters.allBonds,
    isTeamMember,
    page,
    rowsPerPage,
    orderBy,
    order,
    handleSort,
  });

  const meta = {
    mode,
    isTeamMember,
    pageConfig: filters.pageConfig,
    dispatch,
    lawfirmId: filters.lawfirmId,
  };

  const filtersGroup = {
    filterDialogOpen: filters.filterDialogOpen,
    setFilterDialogOpen: filters.setFilterDialogOpen,
    filters: filters.filters,
    setFilters: filters.setFilters,
    bondTypes: filters.bondTypes,
    lawFirms: filters.lawFirms,
    attorneys: filters.attorneys,
    professionalGuardians: filters.professionalGuardians,
    estates: filters.estates,
    principals: filters.principals,
    allBonds: filters.allBonds,
    filteredData: filters.filteredData,
    loading: filters.loading,
    bondNumberOptions: filters.bondNumberOptions,
    fetchInitialData: filters.fetchInitialData,
    applyFilters: filters.applyFilters,
    clearFilters: filters.clearFilters,
    availableSubcategories: filters.availableSubcategories,
    availablePrincipals: filters.availablePrincipals,
    availableEstates: filters.availableEstates,
    dispatchArchiveFilters: filters.dispatchArchiveFilters,
    principalFilterOptions: filters.principalFilterOptions,
    estateFilterOptions: filters.estateFilterOptions,
    refreshToolbar: filters.refreshToolbar,
    onPendingApprovalChip: filters.onPendingApprovalChip,
  };

  const tableGroup = {
    filteredRows: table.filteredRows,
    tableColumns: table.tableColumns,
    tableColSpan: table.tableColSpan,
    pendingCount: table.pendingCount,
    noPendingBonds: table.noPendingBonds,
    archiveBondNumberOptions: table.archiveBondNumberOptions,
    handleSort: table.handleSort,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    orderBy,
    order,
  };

  const constants = useMemo(
    () => ({
      getCancellationInstructionsList,
      lawFirmStatusOptions,
      archiveStatusOptions,
      capitalizeFirst,
      isNameChangeEndorsement,
      getReleaseRiderUrl,
      buildEndorsementFormData,
    }),
    []
  );

  return {
    meta,
    filters: filtersGroup,
    table: tableGroup,
    dialogs,
    constants,
  };
}
